/* Mirror Teeinblue's processing state; leave its upload and editor handlers intact. */
(() => {
  const photoSelector = '#teeFormInputs .tee-field--photo .tee-photo';
  const aiEffects = new Set(['image_filter', 'imagenhub:image_filter', 'gpt:image_filter',
    'gemini:image_filter', 'flux_kontext:image_filter']);
  let campaignId;
  let requestedUrl;
  let aiLayers = new Set();
  let frame;
  let dialog;
  let currentPhoto;
  const dismissed = new Set();

  function readAiLayers(value, result) {
    if (!value || typeof value !== 'object') return;
    if (value.id && Array.isArray(value.effects) && value.effects.some(effect => aiEffects.has(effect.type))) {
      result.add(String(value.id));
    }
    Object.values(value).forEach(child => {
      if (child && typeof child === 'object') readAiLayers(child, result);
    });
  }

  function loadConfiguration() {
    const id = String(window.teeinblueCampaign?.productId || '');
    if (!id) return;
    if (campaignId !== id) {
      campaignId = id;
      requestedUrl = null;
      aiLayers = new Set();
    }
    // Reuse the campaign endpoint the app loaded, rather than guessing a shop or product handle.
    const resource = performance.getEntriesByType('resource').find(entry => {
      try {
        const url = new URL(entry.name);
        return url.protocol === 'https:' && (url.hostname === 'teeinblue.com' || url.hostname.endsWith('.teeinblue.com')) &&
          url.pathname.endsWith(`/campaigns/${id}.json`);
      } catch { return false; }
    });
    if (!resource || requestedUrl === resource.name) return;
    requestedUrl = resource.name;
    fetch(resource.name).then(response => {
      if (!response.ok) throw new Error('Campaign configuration unavailable');
      return response.json();
    }).then(data => {
      if (campaignId !== id) return;
      const result = new Set();
      readAiLayers(data.artworks, result);
      aiLayers = result;
      schedule();
    }).catch(() => {
      // If configuration is unavailable, the original inline loader remains usable.
    });
  }

  function dismiss() {
    if (currentPhoto) dismissed.add(currentPhoto);
    close();
  }

  function createDialog() {
    if (dialog) return;
    dialog = document.createElement('dialog');
    dialog.className = 'gp-ai-loading';
    dialog.setAttribute('aria-labelledby', 'gp-ai-loading-title');
    dialog.setAttribute('aria-describedby', 'gp-ai-loading-message');
    dialog.innerHTML = `
      <button class="gp-ai-loading__close" type="button" aria-label="Close progress dialog">×</button>
      <div class="gp-ai-loading__intro">
        <div class="gp-ai-loading__magic" aria-hidden="true">
          <svg viewBox="0 0 100 100" fill="currentColor"><path d="M38 20c5 23 7 25 30 30-23 5-25 7-30 30-5-23-7-25-30-30 23-5 25-7 30-30Z"/><path d="M78 13c2 10 3 11 13 13-10 2-11 3-13 13-2-10-3-11-13-13 10-2 11-3 13-13ZM79 62c3 13 4 14 17 17-13 3-14 4-17 17-3-13-4-14-17-17 13-3 14-4 17-17Z"/></svg>
        </div>
        <p class="gp-ai-loading__brand">Magic Studio</p>
        <h2 id="gp-ai-loading-title">Your photo is being prepared</h2>
        <div class="gp-ai-loading__percent" aria-hidden="true">…</div>
        <p class="gp-ai-loading__estimate">Estimated progress</p>
      </div>
      <div class="gp-ai-loading__track" role="progressbar" aria-label="Estimated photo processing progress" aria-valuemin="0" aria-valuemax="100"><span></span></div>
      <p class="gp-ai-loading__message" id="gp-ai-loading-message" role="status">Creating your personalized artwork. Please keep this page open.</p>`;
    dialog.querySelector('button').addEventListener('click', dismiss);
    dialog.addEventListener('cancel', event => { event.preventDefault(); dismiss(); });
    document.body.append(dialog);
  }

  function close() {
    if (dialog?.open) {
      dialog.close();
      // The native editor may have opened while our top-layer dialog was visible.
      const editor = document.querySelector('.tee-dialog-image-placement');
      const action = editor?.querySelector('.tee-dcontent-footer .tee-btn--primary');
      if (action?.getClientRects().length) action.focus({ preventScroll: true });
    }
    document.documentElement.classList.remove('gp-ai-loading-open');
    currentPhoto = null;
  }

  function refresh() {
    frame = null;
    loadConfiguration();
    const photos = [...document.querySelectorAll(photoSelector)];
    for (const photo of dismissed) {
      if (!photo.isConnected || !photo.querySelector('.tee-photo-uploading')) dismissed.delete(photo);
    }
    const processing = photos.filter(photo => {
      const field = photo.closest('.tee-field--photo');
      const isAi = aiLayers.has(field.id.replace(/^tee-field--/, ''));
      photo.classList.toggle('gp-photo-upload--ai', isAi);
      return isAi && photo.querySelector('.tee-photo-uploading') && !photo.querySelector('.tee-photo-error') && !dismissed.has(photo);
    });
    if (!processing.length) { close(); return; }
    createDialog();
    currentPhoto = processing[0];
    const raw = currentPhoto.querySelector('.tee-photo-uploading .tee-progress-percent')?.textContent;
    const parsed = parseFloat(raw);
    const hasProgress = Number.isFinite(parsed);
    // Never infer completion from a percentage. The app removes its loader on success/error.
    const percent = hasProgress ? Math.max(0, Math.min(99, Math.floor(parsed))) : null;
    dialog.querySelector('.gp-ai-loading__percent').textContent = hasProgress ? `${percent}%` : '…';
    dialog.querySelector('.gp-ai-loading__estimate').hidden = !hasProgress;
    const track = dialog.querySelector('.gp-ai-loading__track');
    track.classList.toggle('is-indeterminate', !hasProgress);
    if (hasProgress) {
      track.setAttribute('aria-valuenow', String(percent));
      track.style.setProperty('--gp-ai-progress', `${percent}%`);
    } else {
      track.removeAttribute('aria-valuenow');
      track.style.removeProperty('--gp-ai-progress');
    }
    if (!dialog.open) {
      dialog.showModal();
      document.documentElement.classList.add('gp-ai-loading-open');
    }
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(refresh);
  }

  new MutationObserver(records => {
    if (records.some(record => record.target.parentElement?.closest('#teeFormInputs') ||
      record.target.closest?.('#teeFormInputs') || [...record.addedNodes, ...record.removedNodes].some(node =>
        node.nodeType === 1 && (node.matches('#teeFormInputs') || node.querySelector('#teeFormInputs'))))) schedule();
  }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style', 'class'] });
  for (const event of ['teeinblue-event-component-injected', 'teeinblue-event-campaign-loaded', 'teeinblue-event-page-changed']) {
    document.addEventListener(event, schedule);
  }
  window.addEventListener('load', schedule);
  window.addEventListener('pagehide', close);
  schedule();
})();
